import type { ReactNode } from 'react';
import { SectionTitle } from '@/components/brand';

/**
 * Einheitlicher Page-Header im §9-Styleguide-Look:
 *
 *   [ EYEBROW — ALLES GROSS ]
 *   Page-Titel in Blau · teilweise unterstrichen
 *   Optional eine Beschreibung darunter.
 *
 * Rechts davon ein Actions-Slot für Buttons.
 */
export interface PageHeaderProps {
  title: ReactNode;
  /** Kleiner grauer Overline-Text darüber */
  eyebrow?: ReactNode;
  /** Beschreibung unter dem Titel */
  description?: ReactNode;
  /** Buttons / Actions rechts */
  actions?: ReactNode;
  /** Wie viel % des Titels soll unterstrichen sein (0.0–1.0). Default 0.32 */
  underline?: number;
  /** Größe des Titels in px. Default 26 */
  titleSize?: number;
}

export function PageHeader({
  title,
  eyebrow,
  description,
  actions,
  underline = 0.32,
  titleSize = 26,
}: PageHeaderProps) {
  // Mobile (<sm): Titel + Actions stacken vertikal, Actions wrappen falls
  // mehrere Buttons. Sonst werden Buttons wie „+ Neuer Kunde" auf 375 px
  // Viewport rechts abgeschnitten (Bug-Report 2026-06-17).
  return (
    <div className="mb-6 flex flex-col items-stretch gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
      <div>
        {eyebrow && (
          <div className="mb-2 text-[10px] font-medium uppercase tracking-widest text-ink-muted">
            {eyebrow}
          </div>
        )}
        <SectionTitle size={titleSize} underline={underline}>
          {title}
        </SectionTitle>
        {description && (
          <p className="mt-3 max-w-[560px] text-[13px] text-ink-soft">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:flex-nowrap">
          {actions}
        </div>
      )}
    </div>
  );
}
